import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';

export function AppShell(): JSX.Element {
  return (
    <div className="flex min-h-screen bg-surface-muted">
      <Sidebar />
      <main className="flex min-h-screen flex-1 flex-col">
        <Outlet />
      </main>
    </div>
  );
}

interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Small uppercase label, rendered as a leading section marker. */
  eyebrow?: React.ReactNode;
  actions?: React.ReactNode;
}

/**
 * Compact page rail — single horizontal stripe sitting below the global Topbar.
 * The Topbar is the source of truth for the page name; this rail is the
 * *context rail*: section marker, contextual signal data, and primary actions.
 *
 * `title` is kept in the prop API for backwards-compat with all callers but is
 * intentionally not rendered — putting it here would just duplicate the Topbar.
 *
 * Returns `null` when there's nothing contextual to show — keeps quiet pages
 * (no eyebrow, no subtitle, no actions) from getting an empty stripe.
 *
 * Styled with `.rail-accent` so the whole admin shares one editorial vocabulary.
 */
export function PageHeader({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  title: _title,
  subtitle,
  eyebrow,
  actions,
}: PageHeaderProps): JSX.Element | null {
  if (!eyebrow && !subtitle && !actions) return null;

  return (
    <div className="rail-accent mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line bg-surface px-5 py-2.5">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
        {eyebrow ? (
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-subtle">
            {eyebrow}
          </span>
        ) : null}
        {subtitle ? (
          <span className="min-w-0 truncate text-[12.5px] font-medium leading-tight text-ink-muted">
            {subtitle}
          </span>
        ) : null}
      </div>
      {actions ? (
        <div className="ml-auto flex shrink-0 items-center gap-2">{actions}</div>
      ) : null}
    </div>
  );
}
